import axios from 'axios';

// Use relative URL for production, or absolute for development
const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api';

// Get token from localStorage
const getToken = () => {
  return localStorage.getItem('auth_token');
};

// Set token in localStorage
const setToken = (token) => {
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for session cookies
});

// Request interceptor to add token to headers
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      setToken(null);
      // If not already on login page, redirect to login
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication
export const login = async (username, password) => {
  const response = await api.post('/auth.php', { username, password });
  if (response.data.token) {
    setToken(response.data.token);
  }
  return response;
};

export const logout = async () => {
  try {
    await api.delete('/auth.php');
  } finally {
    setToken(null);
  }
};

export const checkAuth = async () => {
  const response = await api.get('/auth.php');
  if (response.data.token) {
    setToken(response.data.token);
  }
  return response;
};

// Persons
export const getPersons = () => api.get('/persons.php');
export const addPerson = (data) => api.post('/persons.php', data);

// Managers
export const getManagers = () => api.get('/managers.php');

// Cards
export const getCards = () => api.get('/cards.php');
export const addCard = (data) => api.post('/cards.php', data);
export const deleteCard = (id) => api.delete(`/cards.php?id=${id}`);

// Transactions
export const getTransactions = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/transactions.php${queryString ? '?' + queryString : ''}`);
};
export const addTransaction = (data) => api.post('/transactions.php', data);
export const deleteTransaction = (id) => api.delete(`/transactions.php?id=${id}`);

// Withdrawals
export const getWithdrawals = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/withdrawals.php${queryString ? '?' + queryString : ''}`);
};
export const addWithdrawal = (data) => api.post('/withdrawals.php', data);
export const deleteWithdrawal = (id) => api.delete(`/withdrawals.php?id=${id}`);

// Salaries
export const getSalaries = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/salaries.php${queryString ? '?' + queryString : ''}`);
};
export const addSalary = (data) => api.post('/salaries.php', data);
export const deleteSalary = (id) => api.delete(`/salaries.php?id=${id}`);

// SBO
export const getSBO = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/sbo.php${queryString ? '?' + queryString : ''}`);
};
export const addSBO = (data) => api.post('/sbo.php', data);
export const deleteSBO = (id) => api.delete(`/sbo.php?id=${id}`);

// Reports
export const getReport = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/reports.php${queryString ? '?' + queryString : ''}`);
};

// Notes
export const getNotes = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/notes.php${queryString ? '?' + queryString : ''}`);
};
export const addNote = (data) => api.post('/notes.php', data);
export const updateNote = (id, data) => api.put(`/notes.php?id=${id}`, data);
export const deleteNote = (id) => api.delete(`/notes.php?id=${id}`);

